import { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur, num } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trophy, Trash2 } from "lucide-react";

const METRICS = [{ v: "revenue", l: "Fatturato" }, { v: "products", l: "Retail/Prodotti" }, { v: "clients", l: "Clienti" }, { v: "services", l: "Servizi" }];

export default function Obiettivi() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { title: "", scope: "company", target_user_id: "", metric: "revenue", target: 10000, period: "month", reward: "", visible_to_team: true };
  const [form, setForm] = useState(empty);
  const canWrite = user && (user.permissions?.includes("*") || user.permissions?.includes("goals.write"));

  const load = () => api.get("/goals").then((r) => setGoals(r.data));
  useEffect(() => { load(); api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);

  const save = async () => {
    if (!form.title) { toast.error("Titolo obbligatorio"); return; }
    const body = { ...form, target_user_id: form.scope === "individual" ? form.target_user_id : null };
    await api.post("/goals", body); toast.success("Obiettivo creato"); setOpen(false); setForm(empty); load();
  };
  const del = async (g) => { await api.delete(`/goals/${g.id}`); toast.success("Eliminato"); load(); };

  return (
    <div>
      <PageHeader title="Obiettivi" subtitle="Target aziendali e individuali">
        {canWrite && <Button onClick={() => setOpen(true)} data-testid="goal-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo obiettivo</Button>}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {goals.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground col-span-full">Nessun obiettivo attivo.</Card>}
        {goals.map((g) => {
          const isVal = g.metric === "clients" || g.metric === "services";
          return (
            <Card key={g.id} className="p-5">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><span className="font-medium">{g.title}</span></div>
                <div className="flex items-center gap-2"><Badge variant="secondary" className="capitalize">{g.scope === "company" ? "Salone" : "Individuale"}</Badge>{canWrite && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(g)}><Trash2 className="h-4 w-4" /></Button>}</div>
              </div>
              <div className="flex justify-between text-sm mt-3 mb-1"><span className="text-muted-foreground">{METRICS.find((m) => m.v === g.metric)?.l} · {g.period}</span><span className="font-medium tabular">{isVal ? num(g.current) : eur(g.current)} / {isVal ? num(g.target) : eur(g.target)}</span></div>
              <Progress value={g.progress} className="h-2.5" />
              <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>{g.progress}% completato</span>{g.reward && <span>Premio: {g.reward}</span>}</div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-2xl">Nuovo obiettivo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Titolo</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="goal-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Ambito</Label><Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="company">Salone</SelectItem><SelectItem value="individual">Individuale</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Metrica</Label><Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}><SelectTrigger data-testid="goal-metric"><SelectValue /></SelectTrigger><SelectContent>{METRICS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent></Select></div>
            </div>
            {form.scope === "individual" && <div className="space-y-1.5"><Label>Collaboratore</Label><Select value={form.target_user_id} onValueChange={(v) => setForm({ ...form, target_user_id: v })}><SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Target</Label><Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: +e.target.value })} data-testid="goal-target" /></div>
              <div className="space-y-1.5"><Label>Periodo</Label><Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="month">Mese</SelectItem><SelectItem value="quarter">Trimestre</SelectItem><SelectItem value="year">Anno</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1.5"><Label>Premio</Label><Input value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })} /></div>
            <Button className="w-full" onClick={save} data-testid="goal-save">Crea obiettivo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
